// src/components/Consumers/Consumers.tsx
import React, { useState, useEffect } from 'react';
import { consumerService } from '../../services/consumerService';
import { apiKeyService } from '../../services/apiKeyService';
import { permissionService } from '../../services/permissionService';
import { apiServicesService } from '../../services/apiServicesService';
import { Consumer, ApiKey, ApiPermission, ApiService, CreateConsumerRequest, CreateApiKeyRequest } from '../../types';
import { Plus, Edit, Trash2, Key, Shield, ChevronLeft } from 'lucide-react';
import './Consumers.css';

const Consumers: React.FC = () => {
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [selectedConsumer, setSelectedConsumer] = useState<Consumer | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKey | null>(null);
  const [permissions, setPermissions] = useState<ApiPermission[]>([]);
  const [apiServices, setApiServices] = useState<ApiService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [showNewConsumerModal, setShowNewConsumerModal] = useState(false);
  const [showNewApiKeyModal, setShowNewApiKeyModal] = useState(false);
  const [showEditConsumerModal, setShowEditConsumerModal] = useState(false);

  // Form states
  const [consumerForm, setConsumerForm] = useState<CreateConsumerRequest>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  const [newApiKeyForm, setNewApiKeyForm] = useState<CreateApiKeyRequest>({
    consumerId: 0,
    friendlyName: '',
    expiryDate: ''
  });

  useEffect(() => {
    fetchConsumers();
    fetchApiServices();
  }, []);

  const fetchConsumers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await consumerService.getAll();
      setConsumers(response.data);
    } catch (error: any) {
      setError('Failed to load consumers');
      console.error('Error fetching consumers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiServices = async () => {
    try {
      const response = await apiServicesService.getAll();
      setApiServices(response.data);
    } catch (error) {
      console.error('Error fetching API services:', error);
    }
  };

  const selectConsumer = async (consumer: Consumer) => {
    setSelectedConsumer(consumer);
    setSelectedApiKey(null);
    setPermissions([]);

    try {
      const response = await apiKeyService.getByConsumer(consumer.id);
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      setApiKeys([]);
    }
  };

  const selectApiKey = async (apiKey: ApiKey) => {
    setSelectedApiKey(apiKey);

    try {
      const response = await permissionService.getKeyPermissions(apiKey.id);
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions([]);
    }
  };

  const createConsumer = async () => {
    try {
      await consumerService.create(consumerForm);
      setShowNewConsumerModal(false);
      setConsumerForm({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        address: '',
        notes: ''
      });
      fetchConsumers();
    } catch (error: any) {
      alert('Failed to create consumer: ' + (error.response?.data?.message || error.message));
    }
  };

  const updateConsumer = async () => {
    if (!selectedConsumer) return;

    try {
      await consumerService.update(selectedConsumer.id, consumerForm);
      setShowEditConsumerModal(false);
      fetchConsumers();
      setSelectedConsumer(null);
    } catch (error: any) {
      alert('Failed to update consumer: ' + (error.response?.data?.message || error.message));
    }
  };

  const deleteConsumer = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this consumer?')) return;

    try {
      await consumerService.delete(id);
      fetchConsumers();
      if (selectedConsumer?.id === id) {
        setSelectedConsumer(null);
      }
    } catch (error: any) {
      alert('Failed to delete consumer: ' + (error.response?.data?.message || error.message));
    }
  };

  const createApiKey = async () => {
    if (!selectedConsumer) return;

    try {
      const response = await apiKeyService.generate({
        ...newApiKeyForm,
        consumerId: selectedConsumer.id
      });

      const apiKey = response.data;
      if (apiKey.apiKey) {
        alert(`API Key Generated Successfully!\n\n${apiKey.apiKey}\n\nPLEASE SAVE THIS KEY - IT WILL NOT BE SHOWN AGAIN`);
      }

      setShowNewApiKeyModal(false);
      setNewApiKeyForm({
        consumerId: 0,
        friendlyName: '',
        expiryDate: ''
      });
      selectConsumer(selectedConsumer);
    } catch (error: any) {
      alert('Failed to generate API key: ' + (error.response?.data?.message || error.message));
    }
  };

  const updatePermission = async (permissionId: number, hasAccess: boolean, dailyRateLimit?: number) => {
    try {
      await permissionService.updatePermission(permissionId, {
        hasAccess,
        dailyRateLimit
      });

      if (selectedApiKey) {
        selectApiKey(selectedApiKey);
      }
    } catch (error) {
      console.error('Error updating permission:', error);
    }
  };

  const openEditConsumerModal = (consumer: Consumer) => {
    setConsumerForm({
      companyName: consumer.companyName,
      contactName: consumer.contactName || '',
      email: consumer.email || '',
      phone: consumer.phone || '',
      address: consumer.address || '',
      notes: consumer.notes || ''
    });
    setShowEditConsumerModal(true);
  };

  if (loading) return <div className="loading">Loading consumers...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="consumers">
      <div className="page-header">
        <h2 className="page-title">Consumers</h2>
        {!selectedConsumer && (
          <button className="btn btn-primary" onClick={() => setShowNewConsumerModal(true)}>
            <Plus size={16} />
            Add Consumer
          </button>
        )}
      </div>

      {!selectedConsumer ? (
        // Consumer List View
        <table className="table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Contact</th>
              <th>Email</th>
              <th>Phone</th>
              <th>API Keys</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {consumers.map(consumer => (
              <tr key={consumer.id}>
                <td>{consumer.companyName}</td>
                <td>{consumer.contactName || '-'}</td>
                <td>{consumer.email || '-'}</td>
                <td>{consumer.phone || '-'}</td>
                <td>{consumer.apiKeyCount || 0}</td>
                <td>
                  <span className={`status-badge ${consumer.isActive ? 'status-active' : 'status-inactive'}`}>
                    {consumer.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon" onClick={() => selectConsumer(consumer)} title="Manage">
                      <Key size={16} />
                    </button>
                    <button className="btn-icon" onClick={() => openEditConsumerModal(consumer)} title="Edit">
                      <Edit size={16} />
                    </button>
                    <button className="btn-icon btn-icon-danger" onClick={() => deleteConsumer(consumer.id)} title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        // Consumer Detail View
        <div className="consumer-detail">
          <button className="btn btn-secondary mb-3" onClick={() => setSelectedConsumer(null)}>
            <ChevronLeft size={16} />
            Back to Consumers
          </button>

          <div className="card">
            <h3>{selectedConsumer.companyName}</h3>
            <div className="consumer-info">
              <div className="info-item">
                <span className="info-label">Contact:</span>
                <span>{selectedConsumer.contactName || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email:</span>
                <span>{selectedConsumer.email || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone:</span>
                <span>{selectedConsumer.phone || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Created:</span>
                <span>{new Date(selectedConsumer.createdDate).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3>API Keys</h3>
              <button className="btn btn-success btn-sm" onClick={() => setShowNewApiKeyModal(true)}>
                <Plus size={16} />
                Generate Key
              </button>
            </div>

            {apiKeys.length > 0 ? (
              <div className="api-keys-list">
                {apiKeys.map(key => (
                  <div key={key.id} className="api-key-item">
                    <div className="api-key-header">
                      <div>
                        <div className="api-key-name">{key.friendlyName || 'Unnamed Key'}</div>
                        <div className="api-key-prefix">Key: {key.keyPrefix}...</div>
                      </div>
                      <span className={`status-badge ${key.isActive ? 'status-active' : 'status-inactive'}`}>
                        {key.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div className="api-key-meta">
                      Created: {new Date(key.createdDate).toLocaleDateString()}
                      {key.expiryDate && ` • Expires: ${new Date(key.expiryDate).toLocaleDateString()}`}
                      {key.lastUsedDate && ` • Last used: ${new Date(key.lastUsedDate).toLocaleDateString()}`}
                    </div>

                    <button className="btn btn-secondary btn-sm" onClick={() => selectApiKey(key)}>
                      <Shield size={16} />
                      Manage Permissions
                    </button>

                    {selectedApiKey?.id === key.id && permissions.length > 0 && (
                      <div className="permissions-section">
                        <h4>API Permissions</h4>
                        <div className="permissions-list">
                          {permissions.map(perm => (
                            <div key={perm.id} className="permission-item">
                              <div className="permission-info">
                                <Shield className={perm.hasAccess ? 'icon-active' : 'icon-inactive'} size={16} />
                                <span>{perm.serviceName}</span>
                              </div>
                              <div className="permission-controls">
                                <input
                                  type="number"
                                  placeholder="Daily limit"
                                  value={perm.dailyRateLimit || ''}
                                  onChange={(e) => updatePermission(perm.id, perm.hasAccess, parseInt(e.target.value) || undefined)}
                                  className="form-input form-input-sm"
                                />
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={perm.hasAccess}
                                    onChange={(e) => updatePermission(perm.id, e.target.checked, perm.dailyRateLimit)}
                                    className="form-checkbox"
                                  />
                                  Enabled
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Key className="empty-state-icon" size={48} />
                <p className="empty-state-title">No API Keys</p>
                <p className="empty-state-description">Generate an API key to get started</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Consumer Modal */}
      {showNewConsumerModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Add New Consumer</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Company Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={consumerForm.companyName}
                  onChange={(e) => setConsumerForm({...consumerForm, companyName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={consumerForm.contactName}
                  onChange={(e) => setConsumerForm({...consumerForm, contactName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={consumerForm.email}
                  onChange={(e) => setConsumerForm({...consumerForm, email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={consumerForm.phone}
                  onChange={(e) => setConsumerForm({...consumerForm, phone: e.target.value})}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNewConsumerModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={createConsumer}>
                Create Consumer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Consumer Modal */}
      {showEditConsumerModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Edit Consumer</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Company Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={consumerForm.companyName}
                  onChange={(e) => setConsumerForm({...consumerForm, companyName: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={consumerForm.contactName}
                  onChange={(e) => setConsumerForm({...consumerForm, contactName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={consumerForm.email}
                  onChange={(e) => setConsumerForm({...consumerForm, email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={consumerForm.phone}
                  onChange={(e) => setConsumerForm({...consumerForm, phone: e.target.value})}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditConsumerModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={updateConsumer}>
                Update Consumer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New API Key Modal */}
      {showNewApiKeyModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">Generate New API Key</h3>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Friendly Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Production API Key"
                  value={newApiKeyForm.friendlyName}
                  onChange={(e) => setNewApiKeyForm({...newApiKeyForm, friendlyName: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Expiry Date (Optional)</label>
                <input
                  type="date"
                  className="form-input"
                  value={newApiKeyForm.expiryDate}
                  onChange={(e) => setNewApiKeyForm({...newApiKeyForm, expiryDate: e.target.value})}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNewApiKeyModal(false)}>
                Cancel
              </button>
              <button className="btn btn-success" onClick={createApiKey}>
                Generate Key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Consumers;
